import { useQuery } from '@tanstack/react-query';
import { WhatsAppOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Form, Input, Radio, Select, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { useFormValues } from './useFormValues';
import {
  addCaseActivity,
  assignCase,
  fetchStaff,
  statusMoves,
  transitionCase,
  type CaseActivityType,
  type CaseStatus,
  type ServiceCase,
} from './api';
import { statusLabel } from './labels';

const { Text } = Typography;

export interface CaseDrawerProps {
  open: boolean;
  serviceCase: ServiceCase;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Runs one API call for a drawer. The server's message is shown as written
 * (CLAUDE.md §10): it already says what happened and what to do.
 */
function useSubmit(onDone: () => void): {
  submitting: boolean;
  failure: string | null;
  clear: () => void;
  run: (call: () => Promise<unknown>) => Promise<void>;
} {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const run = async (call: () => Promise<unknown>): Promise<void> => {
    setSubmitting(true);
    setFailure(null);
    try {
      await call();
      onDone();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('serviceDesk.drawer.failed'));
    } finally {
      setSubmitting(false);
    }
  };
  return { submitting, failure, clear: () => { setFailure(null); }, run };
}

const blank = (v: string | undefined): boolean => v === undefined || v.trim().length === 0;

/** Add note and Log response share a shape: one text box, one activity type. */
function ActivityDrawer({
  type,
  open,
  serviceCase,
  onClose,
  onDone,
}: CaseDrawerProps & { type: Extract<CaseActivityType, 'NOTE' | 'RESPONSE'> }): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ note: string }>();
  const { values, onValuesChange, reset } = useFormValues<{ note: string }>();
  const { note } = values;
  const { submitting, failure, clear, run } = useSubmit(() => {
    form.resetFields();
    reset();
    onDone();
  });
  const isNote = type === 'NOTE';
  const consequence = isNote
    ? t('serviceDesk.drawer.noteConsequence')
    : serviceCase.firstResponseAt === null
      ? t('serviceDesk.drawer.responseConsequenceFirst')
      : t('serviceDesk.drawer.responseConsequenceLater');

  return (
    <ActionDrawer
      open={open}
      title={t(isNote ? 'serviceDesk.drawer.noteTitle' : 'serviceDesk.drawer.responseTitle')}
      submitLabel={t(isNote ? 'serviceDesk.drawer.noteSubmit' : 'serviceDesk.drawer.responseSubmit')}
      submitting={submitting}
      submitDisabled={blank(note)}
      consequence={consequence}
      onSubmit={() => void run(() => addCaseActivity(serviceCase.uid, { type, note: (note ?? '').trim() }))}
      onClose={() => { clear(); onClose(); }}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
    >
      <Form form={form} onValuesChange={onValuesChange} layout="vertical" requiredMark={false}>
        <Form.Item name="note" label={t(isNote ? 'serviceDesk.drawer.noteLabel' : 'serviceDesk.drawer.responseLabel')}>
          <Input.TextArea rows={4} maxLength={5000} autoFocus />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}

export const NoteDrawer = (props: CaseDrawerProps): ReactNode => <ActivityDrawer {...props} type="NOTE" />;
export const ResponseDrawer = (props: CaseDrawerProps): ReactNode => <ActivityDrawer {...props} type="RESPONSE" />;

/**
 * NKS has no WhatsApp Business API (CLAUDE.md §13e): the API logs the message
 * and returns a wa.me link, and staff press Send in their own WhatsApp.
 *
 * Two steps on purpose. The link is shown as a real anchor AFTER the message is
 * logged, never opened by script: a window.open after an await is a blocked
 * popup on iOS and in an installed PWA, and the toast would claim success.
 */
export function WhatsAppDrawer({ open, serviceCase, onClose, onDone }: CaseDrawerProps): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ note: string }>();
  const { values, onValuesChange, reset } = useFormValues<{ note: string }>();
  const { note } = values;
  const [waLink, setWaLink] = useState<string | null>(null);
  const [logged, setLogged] = useState(false);
  const { submitting, failure, clear, run } = useSubmit(() => {
    form.resetFields();
    reset();
    setLogged(true);
  });

  const finish = (): void => {
    clear();
    if (logged) onDone();
    else onClose();
  };

  return (
    <ActionDrawer
      open={open}
      title={t('serviceDesk.drawer.waTitle')}
      submitLabel={t('serviceDesk.drawer.waSubmit')}
      submitting={submitting}
      submitDisabled={blank(note)}
      consequence={logged ? undefined : t('serviceDesk.drawer.waConsequence', { phone: serviceCase.customer.phone })}
      onSubmit={() =>
        void run(async () => {
          const result = await addCaseActivity(serviceCase.uid, { type: 'WHATSAPP', note: (note ?? '').trim() });
          setWaLink(result.waLink ?? null);
        })
      }
      onClose={finish}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
      footer={
        logged ? (
          <Flex justify="flex-end" gap={8}>
            <Button size="large" onClick={finish}>
              {t('serviceDesk.drawer.waDone')}
            </Button>
            {waLink !== null && (
              <Button
                size="large"
                type="primary"
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                icon={<WhatsAppOutlined />}
              >
                {t('serviceDesk.drawer.waOpen')}
              </Button>
            )}
          </Flex>
        ) : undefined
      }
    >
      {logged ? (
        <Alert type="success" showIcon message={t('serviceDesk.drawer.waReady', { phone: serviceCase.customer.phone })} />
      ) : (
        <Form form={form} onValuesChange={onValuesChange} layout="vertical" requiredMark={false}>
          <Form.Item name="note" label={t('serviceDesk.drawer.waLabel')} extra={t('serviceDesk.drawer.waHelp')}>
            <Input.TextArea rows={4} maxLength={1000} autoFocus />
          </Form.Item>
        </Form>
      )}
    </ActionDrawer>
  );
}

export function StatusDrawer({ open, serviceCase, onClose, onDone }: CaseDrawerProps): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ to: CaseStatus; reason: string }>();
  const { values, onValuesChange, reset } = useFormValues<{ to: CaseStatus; reason: string }>();
  const { to, reason } = values;
  const { submitting, failure, clear, run } = useSubmit(() => {
    form.resetFields();
    reset();
    onDone();
  });
  const moves = statusMoves(serviceCase.status);
  // Reopening a resolved case is the one move the API demands a reason for.
  const reopening = serviceCase.status === 'RESOLVED' && to === 'IN_PROGRESS';

  return (
    <ActionDrawer
      open={open}
      title={t('serviceDesk.drawer.statusTitle')}
      submitLabel={t('serviceDesk.drawer.statusSubmit')}
      submitting={submitting}
      submitDisabled={to === undefined || (reopening && blank(reason))}
      consequence={
        to !== undefined
          ? t('serviceDesk.drawer.statusConsequence', {
              caseNo: serviceCase.caseNo,
              from: statusLabel(t, serviceCase.status),
              to: statusLabel(t, to),
            })
          : t('serviceDesk.drawer.statusPick')
      }
      onSubmit={() =>
        void run(() =>
          transitionCase(serviceCase.uid, {
            to: to as CaseStatus,
            ...(blank(reason) ? {} : { reason: (reason ?? '').trim() }),
          }),
        )
      }
      onClose={() => { clear(); onClose(); }}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
    >
      <Form form={form} onValuesChange={onValuesChange} layout="vertical" requiredMark={false}>
        <Form.Item name="to" label={t('serviceDesk.drawer.statusLabel')}>
          <Radio.Group
            options={moves.map((s) => ({ value: s, label: statusLabel(t, s) }))}
            optionType="button"
            buttonStyle="solid"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label={t(reopening ? 'serviceDesk.drawer.statusReasonReopen' : 'serviceDesk.drawer.statusReason')}
          extra={reopening ? t('serviceDesk.drawer.statusReasonRequired') : undefined}
        >
          <Input.TextArea rows={3} maxLength={255} />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}

export function ResolveDrawer({ open, serviceCase, onClose, onDone }: CaseDrawerProps): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ resolution: string; score?: number }>();
  const { values, onValuesChange, reset } = useFormValues<{ resolution: string; score?: number }>();
  const { resolution, score } = values;
  const { submitting, failure, clear, run } = useSubmit(() => {
    form.resetFields();
    reset();
    onDone();
  });

  return (
    <ActionDrawer
      open={open}
      title={t('serviceDesk.drawer.resolveTitle')}
      submitLabel={t('serviceDesk.drawer.resolveSubmit')}
      submitting={submitting}
      submitDisabled={blank(resolution)}
      consequence={t('serviceDesk.drawer.resolveConsequence', { caseNo: serviceCase.caseNo })}
      onSubmit={() =>
        void run(() =>
          transitionCase(serviceCase.uid, {
            to: 'RESOLVED',
            resolution: (resolution ?? '').trim(),
            ...(score !== undefined ? { satisfactionScore: score } : {}),
          }),
        )
      }
      onClose={() => { clear(); onClose(); }}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
    >
      <Form form={form} onValuesChange={onValuesChange} layout="vertical" requiredMark={false}>
        <Form.Item
          name="resolution"
          label={t('serviceDesk.drawer.resolveLabel')}
          extra={blank(resolution) ? t('serviceDesk.drawer.resolveRequired') : undefined}
        >
          <Input.TextArea rows={4} maxLength={5000} autoFocus />
        </Form.Item>
        <Form.Item name="score" label={t('serviceDesk.drawer.resolveScore')} extra={t('serviceDesk.drawer.resolveScoreHelp')}>
          <Radio.Group
            options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }))}
            optionType="button"
          />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}

export function ReassignDrawer({ open, serviceCase, onClose, onDone }: CaseDrawerProps): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ ownerUid: string; reason?: string }>();
  const { values, onValuesChange, reset } = useFormValues<{ ownerUid: string; reason?: string }>();
  const { ownerUid, reason } = values;
  const users = useQuery({ queryKey: ['service-desk', 'staff'], queryFn: fetchStaff, enabled: open });
  const { submitting, failure, clear, run } = useSubmit(() => {
    form.resetFields();
    reset();
    onDone();
  });
  const choices = (users.data ?? []).filter((u) => u.isActive && u.uid !== serviceCase.owner.uid);
  const chosen = choices.find((u) => u.uid === ownerUid);

  return (
    <ActionDrawer
      open={open}
      title={t('serviceDesk.drawer.reassignTitle')}
      submitLabel={t('serviceDesk.drawer.reassignSubmit')}
      submitting={submitting}
      submitDisabled={ownerUid === undefined}
      consequence={
        chosen !== undefined
          ? t('serviceDesk.drawer.reassignConsequence', {
              caseNo: serviceCase.caseNo,
              from: serviceCase.owner.name,
              to: chosen.name,
            })
          : t('serviceDesk.drawer.reassignRequired')
      }
      onSubmit={() =>
        void run(() =>
          assignCase(serviceCase.uid, {
            ownerUid: ownerUid as string,
            ...(blank(reason) ? {} : { reason: (reason ?? '').trim() }),
          }),
        )
      }
      onClose={() => { clear(); onClose(); }}
      error={
        failure !== null ? (
          <Text type="danger">{failure}</Text>
        ) : users.isError ? (
          <Text type="danger">
            {users.error instanceof ApiError ? users.error.message : t('serviceDesk.drawer.reassignNoUsers')}
          </Text>
        ) : undefined
      }
    >
      <Form form={form} onValuesChange={onValuesChange} layout="vertical" requiredMark={false}>
        <Form.Item name="ownerUid" label={t('serviceDesk.drawer.reassignLabel')}>
          <Select
            size="large"
            showSearch
            optionFilterProp="label"
            loading={users.isLoading}
            options={choices.map((u) => ({ value: u.uid, label: u.name }))}
          />
        </Form.Item>
        <Form.Item name="reason" label={t('serviceDesk.drawer.reassignReason')}>
          <Input maxLength={255} />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}
